import { useMemo } from "react";
import { useLoaderData, useLocation, useNavigate, useSearchParams, useSubmit } from "react-router-dom";

import UserInfo from "src/components/common/userInfo";
import ServerFooter from "src/components/common/ServerFooter";
import {
    getDateEndFromWeekNum,
    getDatefromWeekNum,
    pmEmails,
    standbyWeeks,
    statusColorCode,
} from "src/components/common/functions";
import Export from "./export";

type EOTDayForm = {
    availablity?: string;
    yard?: string;
};

type EOTWeekForm = {
    unlock?: boolean;
    status?: string;
    form?: EOTDayForm[];
};

type EOTPerson = {
    email: string;
    userName: string;
    firstName?: string;
    lastName?: string;
    hireDate: string;
    quitDate?: string;
    outside?: string;
    residence?: string;
    phoneNumber?: string;
    lanId?: string;
    [week: string]: any;
};

type LoaderData = {
    forms: EOTPerson[];
    week: string;
    weekEnd?: string;
};

const weekdayIndexes = [4, 5, 6, 7, 8, 9, 10] as const;
const weekdayLabels = ["Friday", "Saturday", "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday"] as const;

export default function AllEmergencyOT() {
    const { forms, week, weekEnd } = useLoaderData() as LoaderData;
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const submit = useSubmit();
    const category = searchParams.get("category") ?? "";
    const status = searchParams.get("status") ?? "";
    const filterWords = searchParams.get("q") ?? "";
    const projectManagerEmails = pmEmails as string[];
    const cutoffDate = useMemo(() => (weekEnd ? new Date(weekEnd.replace(/-/g, "/")) : null), [weekEnd]);
    const filterWordsArray = useMemo(
        () => filterWords.toLowerCase().split(" ").filter(value => value.length > 0),
        [filterWords]
    );

    const filteredForms = useMemo(() => forms.filter((form) => {
        if (projectManagerEmails.includes(form.email)) return false;
        if (!cutoffDate) return false;
        if (status && form[week]?.status !== status) return false;
        if (category && form.outside !== category) return false;
        const hireDate = new Date(form.hireDate.replace(/-/g, "/"));
        if (cutoffDate <= hireDate) return false;
        if (form.quitDate) {
            const quitDate = new Date(form.quitDate.replace(/-/g, "/"));
            if (cutoffDate > quitDate) return false;
        }
        if (filterWordsArray.length > 0) {
            const formString = JSON.stringify(Object.values(form)).toLowerCase();
            if (!filterWordsArray.every(value => formString.includes(value))) return false;
        }
        return true;
    }), [forms, cutoffDate, status, week, category, filterWordsArray, projectManagerEmails]);

    const sortedForms = useMemo(
        () => [...filteredForms].sort((a, b) => a.userName.localeCompare(b.userName)),
        [filteredForms]
    );
    const page = Number(searchParams.get("page") ?? 1);
    const limit = Number(searchParams.get("limit") ?? 50);
    const currentForms = useMemo(
        () => sortedForms.slice((page - 1) * limit, page * limit),
        [sortedForms, page, limit]
    );

    const handleUnlock = (form: EOTPerson) => {
        const weekForm = form[week] as EOTWeekForm | undefined;
        const formData = new FormData();
        formData.append("userEmail", form.email);
        formData.append("wk", week);
        formData.append("unlock", weekForm?.unlock ? "no" : "yes");
        formData.append("redirectTo", location.pathname + location.search);
        submit(formData, {
            method: "post",
            action: `/AllEmergencyOT/Unlock${location.search}`,
        });
    };

    return (
        <div className = "cssContent pb8rem">
            <nav className = "my-3 d-flex align-items-end flex-wrap gap-3">
                <div>
                    <label className = "small text-muted fw-bold mb-1" htmlFor = "allEotSearch">Search</label>
                    <input
                        id = "allEotSearch"
                        className = "form-control w-auto"
                        type = "text"
                        value = {filterWords}
                        onChange = {e => updateSearch(setSearchParams, searchParams, "q", e.currentTarget.value, true)}
                    />
                </div>
                <div>
                    <label className = "small text-muted fw-bold mb-1" htmlFor = "allEotWeek">Week</label>
                    <select
                        id = "allEotWeek"
                        className = "form-control w-auto"
                        value = {week}
                        onChange = {e => navigate(`/AllEmergencyOT/${e.currentTarget.value}${location.search}`)}
                    >
                        {standbyWeeks.map((value, index) => (
                            <option key = {index} value = {value}>{getDateEndFromWeekNum(value)}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className = "small text-muted fw-bold mb-1" htmlFor = "allEotStatus">Status</label>
                    <select
                        id = "allEotStatus"
                        className = "form-control w-auto"
                        value = {status}
                        onChange = {e => updateSearch(setSearchParams, searchParams, "status", e.currentTarget.value, true)}
                    >
                        <option value = "">All</option>
                        <option value = "Submitted">Submitted</option>
                        <option value = "Created">Unsubmitted</option>
                    </select>
                </div>
                <div>
                    <label className = "small text-muted fw-bold mb-1" htmlFor = "allEotCategory">Category</label>
                    <select
                        id = "allEotCategory"
                        className = "form-control w-auto"
                        value = {category}
                        onChange = {e => updateSearch(setSearchParams, searchParams, "category", e.currentTarget.value, true)}
                    >
                        <option value = "">All</option>
                        <option value = "locator">Mark and Locator</option>
                        <option value = "qew">QEW</option>
                        <option value = "standby">Standby</option>
                    </select>
                </div>
                {status !== "Created" && filteredForms.length > 0 ? <Export filteredForms = {filteredForms} week = {week} /> : null}
            </nav>

            {!filteredForms.length ? (
                <div className = "cssLoading">Empty</div>
            ) : (
                <>
                    <div className = "table-responsive">
                        <table className = "table text-center tableAllBorder">
                            <thead>
                                <tr className = "stickyHeader">
                                    <th>Name</th>
                                    <th>Unlock</th>
                                    <th>Yard</th>
                                    {weekdayLabels.map((label, index) => (
                                        <th key = {label}>
                                            <div>{label}</div>
                                            <div>{getDatefromWeekNum(week, weekdayIndexes[index])}</div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {currentForms.map((form) => {
                                    const weekForm = (form[week] ?? {}) as EOTWeekForm;
                                    const dayForms = weekForm.form ?? [];
                                    return (
                                        <tr id = {form.email} key = {form.email}>
                                            <td>
                                                <em><UserInfo name = {form.userName} id = {form.email} /></em>
                                            </td>
                                            <td>
                                                <button
                                                    type = "button"
                                                    className = {`btn btn-${weekForm.unlock ? "primary" : "info"}`}
                                                    onClick = {() => handleUnlock(form)}
                                                >
                                                    Set {weekForm.unlock ? "Lock" : "Unlock"}
                                                </button>
                                            </td>
                                            <td>{dayForms[0]?.yard ?? ""}</td>
                                            {weekdayLabels.map((label, index) => (
                                                <td key = {label} style = {{ color: statusColorCode(weekForm.status ?? "") }}>
                                                    <span className = "mx-2">{dayForms[index]?.availablity ?? ""}</span>
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <ServerFooter length = {sortedForms.length} />
                </>
            )}
        </div>
    );
}

function updateSearch(
    setSearchParams: ReturnType<typeof useSearchParams>[1],
    searchParams: URLSearchParams,
    key: string,
    value: string,
    resetPage = false
) {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    if (resetPage) next.set("page", "1");
    setSearchParams(next);
}
